import type {
  AuthenticatedAdmin,
  CreatePlayerCaseRequest,
  PlayerCase,
  UpdatePlayerCaseRequest,
} from "@utopia-well-done-ltd/contracts";
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { asMoment } from "../format.js";

const iso = (utc: string) => (utc ? new Date(`${utc}Z`).toISOString() : null);
const local = (value: string | null) => (value ? new Date(value).toISOString().slice(0, 16) : "");

export function PlayerCases({
  admin,
  canWrite,
  onChanged,
  playerId,
}: {
  admin: AuthenticatedAdmin;
  canWrite: boolean;
  onChanged: () => void;
  playerId: string;
}) {
  const [cases, setCases] = useState<PlayerCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState(false);

  async function load(signal?: AbortSignal) {
    const page = await api.playerCases(playerId, signal);
    setCases(page.cases);
  }

  useEffect(() => {
    const controller = new AbortController();
    api
      .playerCases(playerId, controller.signal)
      .then((page) => setCases(page.cases))
      .catch((caught: unknown) => {
        if (!controller.signal.aborted)
          setError(caught instanceof ApiError ? caught.message : "Could not load cases.");
      });
    return () => controller.abort();
  }, [playerId]);

  async function mutate(action: () => Promise<unknown>) {
    setPending(true);
    setError(null);
    try {
      await action();
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "That change did not go through.");
    } finally {
      setPending(false);
    }
  }

  if (!cases) return error ? <p className="banner">{error}</p> : <p className="empty">Loading…</p>;

  return (
    <div className="case-panel">
      <div className="case-panel-head">
        <div>
          <div className="label-14-strong">Player cases</div>
          <div className="label-13 faint">Support, KYC and responsible-gaming work</div>
        </div>
        {canWrite ? (
          <button
            className="btn btn-sm"
            onClick={() => setCreating((value) => !value)}
            type="button"
          >
            {creating ? "Cancel" : "New case"}
          </button>
        ) : null}
      </div>

      {creating ? (
        <NewCase
          disabled={pending}
          onCreate={(input) =>
            mutate(async () => {
              await api.createPlayerCase(playerId, input);
              setCreating(false);
            })
          }
        />
      ) : null}

      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}

      {cases.length === 0 ? (
        <p className="empty">No cases have been opened for this player.</p>
      ) : (
        <div className="case-list">
          {cases.map((item) => (
            <CaseCard
              admin={admin}
              canWrite={canWrite}
              disabled={pending}
              item={item}
              key={item.id}
              onNote={(body) => mutate(() => api.addPlayerCaseNote(playerId, item.id, body))}
              onUpdate={(change) => mutate(() => api.updatePlayerCase(playerId, item.id, change))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NewCase({
  disabled,
  onCreate,
}: {
  disabled: boolean;
  onCreate: (input: CreatePlayerCaseRequest) => void;
}) {
  const [initialNote, setInitialNote] = useState("");
  const [priority, setPriority] = useState<CreatePlayerCaseRequest["priority"]>("normal");
  const [slaAt, setSlaAt] = useState("");
  const [subject, setSubject] = useState("");
  const [type, setType] = useState<CreatePlayerCaseRequest["type"]>("support");

  function submit(event: FormEvent) {
    event.preventDefault();
    onCreate({ initialNote, priority, slaAt: iso(slaAt), subject, type });
  }

  return (
    <form className="case-create" onSubmit={submit}>
      <label className="field">
        <span>Subject</span>
        <input
          className="input"
          disabled={disabled}
          minLength={3}
          onChange={(event) => setSubject(event.target.value)}
          required
          value={subject}
        />
      </label>
      <label className="field">
        <span>Type</span>
        <select
          className="input"
          disabled={disabled}
          onChange={(event) => setType(event.target.value as CreatePlayerCaseRequest["type"])}
          value={type}
        >
          <option value="support">Support</option>
          <option value="kyc">KYC</option>
          <option value="rg">Responsible Gaming</option>
        </select>
      </label>
      <label className="field">
        <span>Priority</span>
        <select
          className="input"
          disabled={disabled}
          onChange={(event) =>
            setPriority(event.target.value as CreatePlayerCaseRequest["priority"])
          }
          value={priority}
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </label>
      <label className="field">
        <span>SLA due (UTC)</span>
        <input
          className="input"
          disabled={disabled}
          onChange={(event) => setSlaAt(event.target.value)}
          type="datetime-local"
          value={slaAt}
        />
      </label>
      <label className="field case-note-field">
        <span>Initial note</span>
        <textarea
          className="input textarea"
          disabled={disabled}
          onChange={(event) => setInitialNote(event.target.value)}
          required
          rows={3}
          value={initialNote}
        />
      </label>
      <button className="btn" disabled={disabled} type="submit">
        Create case
      </button>
    </form>
  );
}

function CaseCard({
  admin,
  canWrite,
  disabled,
  item,
  onNote,
  onUpdate,
}: {
  admin: AuthenticatedAdmin;
  canWrite: boolean;
  disabled: boolean;
  item: PlayerCase;
  onNote: (body: string) => void;
  onUpdate: (change: UpdatePlayerCaseRequest) => void;
}) {
  const [note, setNote] = useState("");

  return (
    <article className="case-card">
      <div className="case-card-head">
        <div>
          <div className="case-title">
            <span className="label-14-strong">{item.subject}</span>
            <span className={`badge badge-${item.priority}`}>{item.priority}</span>
            <span className={`badge badge-type-${item.type}`}>{item.type}</span>
          </div>
          <div className="label-13 faint">
            Opened {asMoment(item.createdAt)} by {item.createdBy.email}
          </div>
        </div>
        <span className={`badge badge-${item.status}`}>
          <span className="dot" />
          {item.status.replace("_", " ")}
        </span>
      </div>

      <div className="case-controls">
        <label className="field">
          <span>Status</span>
          <select
            className="input"
            disabled={!canWrite || disabled}
            onChange={(event) =>
              onUpdate({ status: event.target.value as UpdatePlayerCaseRequest["status"] })
            }
            value={item.status}
          >
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="field">
          <span>Priority</span>
          <select
            className="input"
            disabled={!canWrite || disabled}
            onChange={(event) =>
              onUpdate({ priority: event.target.value as UpdatePlayerCaseRequest["priority"] })
            }
            value={item.priority}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </label>
        <label className="field">
          <span>SLA due (UTC)</span>
          <input
            className="input"
            disabled={!canWrite || disabled}
            onBlur={(event) => {
              const next = iso(event.target.value);
              if (next !== item.slaAt) onUpdate({ slaAt: next });
            }}
            type="datetime-local"
            defaultValue={local(item.slaAt)}
          />
        </label>
        <div className="field">
          <span>Owner</span>
          <div className="case-owner">
            <span className="label-13">{item.owner?.email ?? "Unassigned"}</span>
            {canWrite ? (
              <button
                className="btn btn-quiet btn-sm"
                disabled={disabled}
                onClick={() => onUpdate({ ownerId: item.owner?.id === admin.id ? null : admin.id })}
                type="button"
              >
                {item.owner?.id === admin.id ? "Unassign" : "Take ownership"}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="case-notes">
        <div className="label-14-strong">Internal notes · {item.notes.length}</div>
        {item.notes.map((entry) => (
          <div className="case-note" key={entry.id}>
            <p>{entry.body}</p>
            <span className="label-13 faint">
              {entry.author.email} · {asMoment(entry.createdAt)}
            </span>
          </div>
        ))}
        {canWrite ? (
          <form
            className="case-note-add"
            onSubmit={(event) => {
              event.preventDefault();
              onNote(note);
              setNote("");
            }}
          >
            <textarea
              aria-label={`Add note to ${item.subject}`}
              className="input textarea case-note-input"
              disabled={disabled}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add an immutable internal note…"
              required
              rows={2}
              value={note}
            />
            <button className="btn btn-sm" disabled={disabled} type="submit">
              Add note
            </button>
          </form>
        ) : null}
      </div>
    </article>
  );
}
